import * as React from 'react';
import { cn } from '@noor/lib/cn';

export interface SplinePoint {
  date: string;
  label: string;
  value: number;
}

export interface AreaSplineChartProps {
  data: SplinePoint[];
  yTicks?: number[];
  formatYTick?: (val: number) => string;
  formatTooltipValue?: (val: number) => string;
  className?: string;
  height?: number;
  curveColor?: string;
  /**
   * Show every Nth x-axis label (first and last always shown; hidden labels
   * keep invisible placeholders so the visible ones stay aligned). Defaults to 1.
   */
  xLabelInterval?: number;
  /** Hide the y-axis tick gutter (the ticks still scale the curve). Defaults to false. */
  showYAxis?: boolean;
  /** Hide the horizontal gridlines. Defaults to false. */
  showGridlines?: boolean;
  /** Extra classes for the x-axis labels (e.g. a muted tone). */
  xLabelClassName?: string;
  /**
   * Tooltip treatment. `card` (default) is the bordered label + value popover
   * above the point; `pill` is the Noor-CPanel vendor-modal treatment (Figma
   * 1100:24884) — a white value-only pill beside a solid curve dot.
   */
  tooltipVariant?: 'card' | 'pill';
}

/**
 * Computes smooth SVG cubic Bezier path commands for a set of [x, y] coordinates.
 */
function getSplinePath(points: [number, number][]): string {
  if (points.length < 2) return '';
  const first = points[0];
  if (!first) return '';
  let d = `M ${first[0]},${first[1]}`;

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(i - 1, 0)] ?? first;
    const p1 = points[i] ?? first;
    const p2 = points[i + 1] ?? first;
    const p3 = points[Math.min(i + 2, points.length - 1)] ?? p2;

    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;

    d += ` C ${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
  }

  return d;
}

export function AreaSplineChart({
  data,
  yTicks = [50000, 40000, 30000, 20000, 10000],
  formatYTick = (val) => `${Math.round(val / 1000)}K`,
  formatTooltipValue = (val) => `${val.toLocaleString()} шт`,
  className,
  height = 215,
  curveColor = '#FF6D00',
  xLabelInterval = 1,
  showYAxis = true,
  showGridlines = true,
  xLabelClassName,
  tooltipVariant = 'card',
}: AreaSplineChartProps) {
  const [hoveredIdx, setHoveredIdx] = React.useState<number | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const minY = Math.min(...yTicks);
  const maxY = Math.max(...yTicks);
  const rangeY = maxY - minY || 1;

  // ViewBox coordinate space: width 760, height 215 (padding 10 top and bottom)
  const chartWidth = 760;
  const padTop = 15;
  const padBottom = 20;
  const usableHeight = height - padTop - padBottom;

  const coords: [number, number][] = React.useMemo(() => {
    if (!data.length) return [];
    const stepX = chartWidth / (data.length - 1 || 1);
    return data.map((d, i) => {
      const x = i * stepX;
      const normalizedY = Math.max(0, Math.min(1, (d.value - minY) / rangeY));
      const y = padTop + (1 - normalizedY) * usableHeight;
      return [x, y];
    });
  }, [data, minY, rangeY, usableHeight, padTop, chartWidth]);

  const linePath = React.useMemo(() => getSplinePath(coords), [coords]);
  const areaPath = React.useMemo(() => {
    if (!coords.length) return '';
    const firstX = coords[0]?.[0] ?? 0;
    const lastX = coords[coords.length - 1]?.[0] ?? chartWidth;
    return `${linePath} L ${lastX},${height} L ${firstX},${height} Z`;
  }, [linePath, coords, height, chartWidth]);

  const activePoint = hoveredIdx !== null ? data[hoveredIdx] : null;
  const activeCoord = hoveredIdx !== null ? coords[hoveredIdx] : null;

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex w-full gap-2 font-sans select-none sm:gap-3',
        className,
      )}
    >
      {/* Y-axis Ticks */}
      {showYAxis ? (
        <div className="flex flex-col justify-between py-1 text-right text-[12px] font-normal text-black select-none dark:text-neutral-300">
          {yTicks.map((tick) => (
            <span key={tick} className="w-8">
              {formatYTick(tick)}
            </span>
          ))}
        </div>
      ) : null}

      {/* Chart Canvas & SVG */}
      <div className="relative flex flex-1 flex-col">
        <div
          className="relative w-full overflow-visible"
          style={{ height }}
          onMouseLeave={() => setHoveredIdx(null)}
        >
          {/* Horizontal Gridlines */}
          {showGridlines ? (
            <svg
              className="pointer-events-none absolute inset-0 size-full overflow-visible"
              preserveAspectRatio="none"
              viewBox={`0 0 ${chartWidth} ${height}`}
            >
              {yTicks.map((tick) => {
                const normalized = (tick - minY) / rangeY;
                const y = padTop + (1 - normalized) * usableHeight;
                return (
                  <line
                    key={tick}
                    x1={0}
                    y1={y}
                    x2={chartWidth}
                    y2={y}
                    stroke="#F1F5F9"
                    strokeWidth="1"
                    strokeDasharray="0"
                  />
                );
              })}
            </svg>
          ) : null}

          {/* Area Fill & Curve */}
          <svg
            className="absolute inset-0 size-full overflow-visible"
            preserveAspectRatio="none"
            viewBox={`0 0 ${chartWidth} ${height}`}
          >
            <defs>
              <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={curveColor} stopOpacity="0.14" />
                <stop offset="100%" stopColor={curveColor} stopOpacity="0.0" />
              </linearGradient>
            </defs>

            {/* Gradient Area */}
            {areaPath && (
              <path
                d={areaPath}
                fill="url(#areaGradient)"
                className="transition-opacity duration-300"
              />
            )}

            {/* Glowing Line */}
            {linePath && (
              <path
                d={linePath}
                fill="none"
                stroke={curveColor}
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* Active Point Indicator */}
            {activeCoord &&
              (tooltipVariant === 'pill' ? (
                <g>
                  <line
                    x1={activeCoord[0]}
                    y1={padTop}
                    x2={activeCoord[0]}
                    y2={height}
                    stroke="#d1d5db"
                    strokeWidth="1"
                    strokeDasharray="3 3"
                  />
                  <circle
                    cx={activeCoord[0]}
                    cy={activeCoord[1]}
                    r="9"
                    fill="#ffffff"
                    opacity="0.9"
                  />
                  <circle
                    cx={activeCoord[0]}
                    cy={activeCoord[1]}
                    r="5"
                    fill={curveColor}
                  />
                </g>
              ) : (
                <g>
                  <line
                    x1={activeCoord[0]}
                    y1={padTop}
                    x2={activeCoord[0]}
                    y2={height}
                    stroke={curveColor}
                    strokeWidth="1"
                    strokeDasharray="3 3"
                    opacity="0.6"
                  />
                  <circle
                    cx={activeCoord[0]}
                    cy={activeCoord[1]}
                    r="5"
                    fill="#ffffff"
                    stroke={curveColor}
                    strokeWidth="3"
                  />
                </g>
              ))}
          </svg>

          {/* Invisible hover trigger columns */}
          <div className="absolute inset-0 flex">
            {coords.map((_, i) => (
              <div
                key={i}
                className="flex-1 cursor-pointer"
                onMouseEnter={() => setHoveredIdx(i)}
              />
            ))}
          </div>

          {/* Tooltip */}
          {activePoint && activeCoord && tooltipVariant === 'card' ? (
            <div
              className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-full rounded-lg border border-border/80 bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-md transition-all duration-150"
              style={{
                left: `${(activeCoord[0] / chartWidth) * 100}%`,
                top: `${activeCoord[1] - 8}px`,
              }}
            >
              <div className="font-semibold">{activePoint.label}</div>
              <div className="text-[11px] text-muted-foreground">
                {formatTooltipValue(activePoint.value)}
              </div>
            </div>
          ) : null}
          {activePoint && activeCoord && tooltipVariant === 'pill' ? (
            <div
              className={cn(
                'pointer-events-none absolute z-20 -translate-y-1/2 rounded-[4px] bg-white px-[6px] py-px text-base whitespace-nowrap text-[#212121] shadow-[0px_2px_8px_-2px_rgba(0,0,0,0.2)]',
                activeCoord[0] / chartWidth > 0.75
                  ? '-translate-x-[calc(100%+12px)]'
                  : 'translate-x-[12px]',
              )}
              style={{
                left: `${(activeCoord[0] / chartWidth) * 100}%`,
                top: `${activeCoord[1]}px`,
              }}
            >
              {formatTooltipValue(activePoint.value)}
            </div>
          ) : null}
        </div>

        {/* X-axis Labels */}
        <div className="mt-3 flex justify-between text-[12px] font-normal text-black dark:text-neutral-300">
          {data.map((item, idx) => {
            const interval = Math.max(1, Math.floor(xLabelInterval));
            const show = idx % interval === 0 || idx === data.length - 1;
            return (
              <span
                key={idx}
                aria-hidden={!show}
                className={cn(
                  'text-center transition-colors',
                  !show && 'invisible',
                  hoveredIdx === idx
                    ? 'font-semibold text-[#ff6d00]'
                    : 'text-black dark:text-neutral-300',
                  hoveredIdx !== idx && xLabelClassName,
                )}
              >
                {item.label}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
